import get from 'lodash/get'
import mapValues from 'lodash/mapValues'
import * as Yup from 'yup'
import { EDatasheetFieldsForEditComplex } from 'core/common/constants/datasheet'
import i18n from 'core/common/i18n/index'
import { youtubeRegex, regexSKU } from 'core/common/utils/regex'
import {
  clearHtml,
  countCharactersWSpace,
} from 'core/containers/ProductDescription/components/ProductTextEditor/utils/sanitize'
import { descriptionTranslate } from 'core/containers/ProductVariation/utils'
import { isValidNumberAnatel } from './anatel'
import { isEANValid } from './eanValidate'
import { isISBNValid } from './isbnValidate'
import { regulationValidate } from './regulationValidate'

export const INVALID_CHARACTERS_REGEX = /^[^/?°®ŧ←↓→øþæßðđŋħˀĸł´«»©„“”µ]*$/

export const DESCRIPTION_INVALID_CHARACTERS_REGEX = /^[^ŧøþæßðđŋħˀĸł«»„µ]*$/

/**
 * Método para aplicar validação para o campo de descrição do editor.
 */
Yup.addMethod(Yup.string, 'description', () => {
  return Yup.string().test(
    'charactersLimitValidate',
    i18n.t('product.validations.charactersLimitDescription'),
    value => {
      const count = countCharactersWSpace(clearHtml(value))
      return !(count < 1 || count > 5000)
    }
  )
})

Yup.addMethod(Yup.string, 'ean', () => {
  return Yup.string().test('eanValidate', i18n.t('product.validations.ean'), isEANValid)
})

Yup.addMethod(Yup.string, 'isbn', () => {
  return Yup.string().test('isbnValidate', i18n.t('product.validations.isbn'), isISBNValid)
})

Yup.addMethod(Yup.string, 'anatel', () => {
  return Yup.string().test(
    'anatelValidate',
    i18n.t('product.validations.anatel'),
    isValidNumberAnatel
  )
})

Yup.addMethod(Yup.string, 'regulation', () => {
  return Yup.string().test(
    'regulationValidate',
    i18n.t('product.validations.regulation'),
    regulationValidate
  )
})

/**
 *
 * Método para validar espaços em branco
 */
Yup.addMethod(Yup.string, 'blockSpaces', message => {
  return Yup.string().test('blockSpaces', message, value => {
    if (value) {
      return /\s/.test(value) ? NaN : value
    }

    return true
  })
})

function isSkuDuplicated(message, othersSKU) {
  return this.test('skuUniqueValidate', message, sku => {
    const skus = othersSKU.filter(value => value.sku).map(value => value.sku)
    const isDuplicated = skus.filter(filteredSku => filteredSku === sku).length > 1
    return !isDuplicated
  })
}

Yup.addMethod(Yup.string, 'skuUnique', isSkuDuplicated)

export const StockAndPriceSchema = (validationType, validationConditionalValue) => ({
  prices: Yup.array()
    .of(
      Yup.object().shape({
        price: Yup.number(),
        list_price: Yup.number(),
      })
    )
    .when(validationType, {
      is: validationConditionalValue,
      then: Yup.array().of(
        Yup.object().shape({
          price: Yup.number()
            .moreThan(0, i18n.t('product.validations.minValue'))
            .max(Yup.ref('list_price'), i18n.t('product.validations.maxPriceComparsion'))
            .required(i18n.t('product.validations.required')),
          list_price: Yup.number()
            .moreThan(0, i18n.t('product.validations.minValue'))
            .max(9999999999, i18n.t('product.validations.maxValue', { count: 9999999999 }))
            .required(i18n.t('product.validations.required')),
        })
      ),
    }),

  stocks: Yup.array()
    .of(
      Yup.object().shape({
        quantity: Yup.number(),
      })
    )
    .when(validationType, {
      is: validationConditionalValue,
      then: Yup.array().of(
        Yup.object().shape({
          quantity: Yup.number()
            .positive()
            .min(0, i18n.t('product.validations.minValue'))
            .max(999999, i18n.t('product.validations.maxValue', { count: 999999 }))
            .required(i18n.t('product.validations.required')),
        })
      ),
    }),
})

function duplicateDescriptionCheck(message, othersDescriptions) {
  return this.test('descriptionUniqueValidate', message, description => {
    const repeatedDescriptions = Object.keys(othersDescriptions)
      .map(key => othersDescriptions[key].description)
      .map(desc => descriptionTranslate(desc))
      .filter(i => i === description?.toLowerCase())

    return repeatedDescriptions.length <= 1
  })
}

Yup.addMethod(Yup.string, 'descriptionUnique', duplicateDescriptionCheck)

const BaseProductSchema = schema => ({
  anatel: Yup.string().anatel(),
  anvisa: Yup.string().regulation(),
  mapa: Yup.string().regulation(),
  sku: schema?.isVariationEnabled
    ? Yup.string().trim(i18n.t('product.validations.required'))
    : Yup.string()
        .trim(i18n.t('product.validations.required'))
        .required(i18n.t('product.validations.required')),
  package: Yup.object().shape({
    standard_packing: Yup.boolean().required(),
    size: Yup.number().when('standard_packing', {
      is: true,
      then: Yup.number().required(i18n.t('product.validations.required')),
    }),
    depth: Yup.number()
      .typeError(i18n.t('product.validations.onlyNumbers'))
      .moreThan(0, i18n.t('product.validations.minValue'))
      .required(i18n.t('product.validations.required')),
    height: Yup.number()
      .typeError(i18n.t('product.validations.onlyNumbers'))
      .moreThan(0, i18n.t('product.validations.minValue'))
      .required(i18n.t('product.validations.required')),
    width: Yup.number()
      .typeError(i18n.t('product.validations.onlyNumbers'))
      .moreThan(0, i18n.t('product.validations.minValue'))
      .required(i18n.t('product.validations.required')),
    weight: Yup.object().shape({
      value: Yup.number()
        .typeError(i18n.t('product.validations.onlyNumbers'))
        .moreThan(0, i18n.t('product.validations.minValue'))
        .max(1510000, i18n.t('product.validations.maxWeight', { count: 1510000, unit: 'g' }))
        .when('unit', {
          is: 'kg',
          then: Yup.number().max(
            1510,
            i18n.t('product.validations.maxWeight', { count: 1510, unit: 'kg' })
          ),
        })
        .required(i18n.t('product.validations.required')),
    }),
  }),
  selectedAttributes: Yup.lazy(attrObj => {
    return schema?.isVariationEnabled
      ? Yup.object().shape(
          mapValues(attrObj, () =>
            Yup.object({
              description: Yup.string()
                .required(i18n.t('product.validations.required'))
                .descriptionUnique(
                  i18n.t('product.validations.duplicatedDescription'),
                  schema?.selectedAttributes
                ),
              value: Yup.array()
                .of(Yup.string())

                .required(i18n.t('product.validations.required')),
            })
          )
        )
      : Yup.object()
  }),
  variations: Yup.array().of(
    Yup.object().shape({
      active: Yup.boolean(),
      sku: Yup.string().when('active', {
        is: true,
        then: Yup.string()
          .required(i18n.t('product.validations.required'))
          .trim(i18n.t('product.validations.required'))
          .skuUnique(i18n.t('product.validations.duplicatedSKU'), schema?.variations)
          .matches(regexSKU, i18n.t('product.validations.onlyCharacters')),
      }),
      ean: Yup.string(),
      ...StockAndPriceSchema('active', true),
      images: Yup.array()
        .of(Yup.string())
        .when('active', {
          is: true,
          then: Yup.array().of(Yup.string()).min(1, i18n.t('product.validations.required')),
        }),
    })
  ),
})

export const NewProductSchema = Yup.lazy(obj => {
  return Yup.object().shape({
    ...BaseProductSchema(obj),
    update_only_front: Yup.boolean().optional(),
    isVariationEnabled: Yup.boolean(),
    brand: Yup.string()
      .min(2, i18n.t('product.validations.minSize', { count: 2 }))
      .max(100, i18n.t('product.validations.maxSize', { count: 100 }))
      .required(i18n.t('product.validations.required')),
    video: Yup.string().matches(youtubeRegex, {
      message: i18n.t('product.validations.video'),
    }),
    uploadedImages: Yup.array()
      .of(Yup.string())
      .when('isVariationEnabled', {
        is: false,
        then: Yup.array().of(Yup.string()).min(1, i18n.t('product.validations.required')),
      }),
    type: Yup.string().trim().required(i18n.t('product.validations.required')),
    name: Yup.string()
      .min(1, i18n.t('product.validations.minSize', { count: 1 }))
      .max(150, i18n.t('product.validations.maxSize', { count: 150 }))
      .required(i18n.t('product.validations.required')),
    ean: Yup.string().ean(),
    ncm: Yup.string().min(10, i18n.t('product.validations.minNcm')),
    sku: Yup.string().when('isEditing', {
      is: true,
      then: Yup.string(),
      otherwise: Yup.string().when('isVariationEnabled', {
        is: true,
        then: Yup.string()
          .trim(i18n.t('product.validations.required'))
          .blockSpaces(i18n.t('product.validations.forbiddenSpaces'))
          .matches(regexSKU, i18n.t('product.validations.onlyCharacters')),
        otherwise: Yup.string()
          .trim(i18n.t('product.validations.required'))
          .blockSpaces(i18n.t('product.validations.forbiddenSpaces'))
          .required(i18n.t('product.validations.required'))
          .matches(regexSKU, i18n.t('product.validations.onlyCharacters')),
      }),
    }),

    isbn: Yup.string().isbn(),
    description: Yup.string().description().required(i18n.t('product.validations.required')),
    datasheet: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().test(
          'forbiden-value',
          i18n.t('product.validations.datasheetTypesValidation'),
          value =>
            !value || !Object.values(EDatasheetFieldsForEditComplex).includes(value.toLowerCase())
        ),
        value: Yup.string(),
      })
    ),
    // base schema for prices and stock
    ...StockAndPriceSchema('isVariationEnabled', false),
  })
})

export const ImproveScoreSchema = Yup.lazy(() => {
  return Yup.object().shape({
    name: Yup.string()
      .min(1, i18n.t('product.validations.minSize', { count: 1 }))
      .max(150, i18n.t('product.validations.maxSize', { count: 150 }))
      .required(i18n.t('product.validations.required')),
    description: Yup.string().description().required(i18n.t('product.validations.required')),
    ean: Yup.string().ean(),
    uploadedImages: Yup.array().of(Yup.string()).min(1, i18n.t('product.validations.required')),
  })
})

export const MatchProductSchema = Yup.lazy(obj => {
  return Yup.object().shape({
    ...BaseProductSchema(obj),
    isVariationEnabled: Yup.boolean(),
    images: Yup.array().of(Yup.string()),
    matchVariations: Yup.array()
      .when('isVariationEnabled', {
        is: true,
        then: Yup.array().min(2, i18n.t('product.validations.required')),
      })
      .of(
        Yup.object().shape({
          selected: Yup.boolean(),
          sku: Yup.string().when('selected', {
            is: true,
            then: Yup.string()
              .trim(i18n.t('product.validations.required'))
              .required(i18n.t('product.validations.required'))
              .skuUnique(i18n.t('product.validations.duplicatedSKU'), obj?.variations),
          }),
          ...StockAndPriceSchema('selected', true),
        })
      ),
    // base schema for prices and stock
    ...StockAndPriceSchema('isVariationEnabled', false),
  })
})

export const ProductInputSearch = Yup.object().shape({
  ean: Yup.string()
    .min(10, i18n.t('product.validations.isbn'))
    .max(14, i18n.t('product.validations.ean'))
    .required(i18n.t('product.validations.required')),
})

export const EditProductSchema = Yup.lazy(scheme => {
  return Yup.object().shape({
    hasVariation: Yup.boolean(),
    prices: Yup.array().when('hasVariation', {
      is: false,
      then: Yup.array().of(
        Yup.object().shape({
          price: Yup.number()
            .min(1, i18n.t('product.validations.minValue'))
            .max(9999999999, i18n.t('product.validations.maxValue', { count: 9999999999 }))
            .required(i18n.t('product.validations.required')),
        })
      ),
    }),
    stocks: Yup.array().when('hasVariation', {
      is: false,
      then: Yup.array().of(
        Yup.object().shape({
          quantity: Yup.number()
            .min(1, i18n.t('product.validations.minValue'))
            .max(999999, i18n.t('product.validations.maxValue', { count: 999999 }))
            .required(i18n.t('product.validations.required')),
        })
      ),
    }),
    variants: scheme.hasVariation
      ? Yup.array().of(
          Yup.object().shape({
            prices: Yup.array().of(
              Yup.object().shape({
                price: Yup.number()
                  .min(1, i18n.t('product.validations.minValue'))
                  .max(Yup.ref('list_price'), i18n.t('product.validations.maxPriceComparsion'))
                  .required(i18n.t('product.validations.required')),
              })
            ),
            list_price: Yup.array().of(
              Yup.object().shape({
                price: Yup.number()
                  .min(1, i18n.t('product.validations.minValue'))
                  .max(9999999999, i18n.t('product.validations.maxValue', { count: 9999999999 }))
                  .required(i18n.t('product.validations.required')),
              })
            ),
            stocks: Yup.array().of(
              Yup.object().shape({
                quantity: Yup.number()
                  .min(1, i18n.t('product.validations.minValue'))
                  .max(999999, i18n.t('product.validations.maxValue', { count: 999999 }))
                  .required(i18n.t('product.validations.required')),
              })
            ),
          })
        )
      : null,
  })
})

export const validateField = (name, errors, touched, alreadySubmitted = false) =>
  get(errors, name) && (get(touched, name) || alreadySubmitted)

export const getErrorMessage = (name, errors, touched, defaultMessage) =>
  validateField(name, errors, touched) ? get(errors, name) : defaultMessage
